import { memo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import {
    hasAccess,
    permissionRoles,
    permissionSections
} from "./config";

const AccessIcon = ({ isGranted }: { readonly isGranted: boolean }) => (
    <Tooltip
        title={isGranted ? 'Has access' : 'No access'}
        arrow
    >
        {isGranted
            ? (
                <CheckCircleRoundedIcon
                    color='success'
                    aria-label='Has access'
                />
            )
            : (
                <LockRoundedIcon
                    color='warning'
                    aria-label='No access'
                />
            )}
    </Tooltip>
);

const Legend = () => (
    <Stack
        direction='row'
        spacing={3}
        sx={{
            px: 3,
            pb: 2
        }}
    >
        {[true, false].map(isGranted => (
            <Stack
                key={String(isGranted)}
                direction='row'
                spacing={1}
                alignItems='center'
            >
                <AccessIcon isGranted={isGranted} />
                <Typography
                    variant='body2'
                    color='text.secondary'
                >
                    {isGranted ? 'Has access' : 'No access'}
                </Typography>
            </Stack>
        ))}
    </Stack>
);

const RolesPermissions = () => (
    <Card
        data-test-id='users.rolesPermissions'
        sx={{
            maxWidth: 880,
            mx: 'auto'
        }}
    >
        <CardHeader
            title='Roles & Permissions'
            subheader='Sections of the merchant portal each role can access'
            sx={{ pb: 2 }}
        />
        <Legend />
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
                size='small'
                sx={{
                    minWidth: 600,
                    '& th': {
                        whiteSpace: 'normal',
                        lineHeight: 1.3
                    },
                    '& th:not(:first-of-type), & td:not(:first-of-type)': {
                        width: 120,
                        px: 1
                    }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Section</TableCell>
                        {permissionRoles.map(({ roleName, label }) => (
                            <TableCell
                                key={roleName}
                                align='center'
                                data-test-id={`users.rolesPermissions.column.${roleName}.header`}
                            >
                                {label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {permissionSections.map(section => (
                        <TableRow
                            key={section.key}
                            hover
                            data-test-id={`users.rolesPermissions.row.${section.key}`}
                        >
                            <TableCell>
                                <Box>
                                    <Typography variant='subtitle2'>
                                        {section.label}
                                    </Typography>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                    >
                                        {section.description}
                                    </Typography>
                                </Box>
                            </TableCell>
                            {permissionRoles.map(({ roleName }) => {
                                const isGranted = hasAccess(section, roleName);

                                return (
                                    <TableCell
                                        key={roleName}
                                        align='center'
                                        data-test-id={`users.rolesPermissions.row.${section.key}.cell.${roleName}`}
                                        data-access={isGranted ? 'granted' : 'denied'}
                                    >
                                        <AccessIcon isGranted={isGranted} />
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Card>
);

export default memo(RolesPermissions);
